import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import ResampleForm from "@/components/resample-form"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { useFieldStatus, useForm, UseFormReturn } from "@/lib/form/form"
import { ParamSchema, ParamSchemaType } from "@/lib/form/schema"
import { SingleValueParamData } from "@/lib/form/types"
import { createValidator } from "@/lib/validators/limit"
import { useEffect, useState } from "react"
import * as y from "yup"

export interface ParamRemarksSheetProps {
  form: UseFormReturn
  showResample?: boolean
}

interface ParamRemarksFormData {
  remark: string
  resample?: SingleValueParamData
  resampleCleared?: boolean
}

export default function ParamRemarksSheet({
  form,
  showResample = true,
}: ParamRemarksSheetProps) {
  const [open, setOpen] = useState(false)
  const [path, setPath] = useState("")
  const [localValue, setLocalValue] = useState("")
  const [schema, setSchema] = useState<ParamSchema | null>(null)
  const remarksForm = useForm()
  const [touched, setTouched] = useState(false)
  const { register, setValue, handleSubmit, reset } = remarksForm
  const status = useFieldStatus({
    form: remarksForm,
  })

  useInit()

  function close() {
    setOpen(false)
  }

  function useInit() {
    useEffect(() => {
      register("remark", {
        validate: createValidator(y.string().min(1).required()),
      })

      form.addEventHandler("clickParamRemarks", (__event, context) => {
        setPath(context.path)
        setSchema(context.schema as ParamSchema)
        setOpen(true)
        setLocalValue(form.getValues(`${context.path}.remark`))

        const resampled = form.getValues(`${context.path}.resample`)
        const resampleCleared = form.getValues(
          `${context.path}.resampleCleared`
        )
        const remark = form.getValues(`${context.path}.remark`)
        setValue("remark", remark)
        setValue("resample", resampled)
        setValue("resampleCleared", resampleCleared)

        if (!resampled?.remark && remark) {
          setTouched(true)
        }
      })
    }, [form, remarksForm, register])

    useEffect(() => {
      if (!open) {
        setTimeout(() => {
          setLocalValue("")
          setPath("")
          reset()
          setSchema(null)
          setTouched(false)
        }, 500)
      }
    }, [open])
  }

  function handleChangeRemark(value: string) {
    setLocalValue(value)
    setTouched(true)
    setValue("remark", value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  // TODO: add stricter types
  function submit(data: any) {
    const formData = data as ParamRemarksFormData

    form.setValue(`${path}.remark`, formData.remark, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
    form.setValue(`${path}.resample`, formData.resample, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    form.setValue(`${path}.resampleCleared`, formData.resampleCleared, {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: true,
    })

    close()
  }

  function isResampleAllowed(
    schema: ParamSchema | null
  ): schema is ParamSchema {
    if (!showResample) {
      return false
    }

    return !!(schema && schema.type !== ParamSchemaType.CHOICE)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" title="Fill remarks on parameter">
        <form onSubmit={handleSubmit(submit)}>
          <SheetHeader className="pb-0">
            <SheetTitle className="font-semibold text-xl text-button-black">
              Fill Remarks
            </SheetTitle>
          </SheetHeader>

          <div className="px-4 pb-8">
            <Textarea
              className="mt-6"
              placeholder="Describe the issue and action taken"
              value={localValue}
              onChange={handleChangeRemark}
            />

            {isResampleAllowed(schema) && (
              <ResampleForm
                className="mt-5"
                schema={schema}
                path="resample"
                form={remarksForm}
                disabled={!touched}
              />
            )}
          </div>

          <SheetFooter className="p-0">
            <Bottombar className="bg-white flex gap-4">
              <Button variant="outline" onClick={close}>
                Back
              </Button>
              <Button
                className="grow"
                type="submit"
                disabled={status !== "VALID"}
              >
                Submit
              </Button>
            </Bottombar>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
